import * as ENVIRONMENTS from 'constants/environments'
import { env } from 'utils/env'
import { Box, Typography, useTheme, IconButton, Stack } from '@mui/material'
import { Helmet } from 'react-helmet'
import SectionCard from 'components/shared/SectionCard'
import { RowDataType } from 'components/runs/BasicInfoTableConfig'
import { TopNavMenu } from 'components/navigationTopBar'
import useFlagCondition from 'components/shared/useFlagCondition'
import { isExternalDemo } from 'config'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import useErrorSlice from 'redux/slices/hooks/useErrorSlice'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import React from 'react'
import DownloadIndicator from 'components/shared/DownloadIndicator'
import useDownloadsSlice from 'redux/slices/hooks/useDownloadsSlice'
import { DownloadItem } from 'components/shared/DownloadPanel'
import useFileDownload from 'utils/useFileDownload'
import useCellVisualizationUrlParams from '../useCellVisualizationUrlParams'
import { NavTopBarRightSideButtons } from './NavTopBarRightSideButtons'
import TopBarBase from './TopBarBase'

const ToolbarBox = ({ children }: React.PropsWithChildren<unknown>) => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>{children}</Box>
)

export const CellVisualizationNavTopBar = (): JSX.Element => {
  const demoEnabled = useFlagCondition('demoEnabled')
  const { sessionId, isPreFilter } = useCellVisualizationUrlParams()
  const theme = useTheme()
  const {
    error: { errored },
  } = useErrorSlice()

  // Enhanced download functionality
  const {
    downloads,
    updateDownloadItem,
    removeDownloadItem,
    autoDownload,
    setAutoDownloadEnabled,
  } = useDownloadsSlice()
  const { downloadFile } = useFileDownload()

  const onDownload = async (item: DownloadItem, fileName: string) => {
    // Only allow download if the item is ready with a valid downloadUrl
    if (!item.downloadUrl || item.status !== 'ready') return

    // Update status to downloading
    updateDownloadItem(item.id, { status: 'downloading' })

    try {
      await downloadFile(item.downloadUrl, fileName, {
        onSuccess: () => {
          updateDownloadItem(item.id, { status: 'completed' })
        },
        onError: (error) => {
          console.error('Download failed:', error)
          updateDownloadItem(item.id, {
            status: 'error',
            errorMessage: error.message,
          })
        },
      })
    } catch (error) {
      console.error('Download failed:', error)
      updateDownloadItem(item.id, {
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  const onCancel = (itemId: string) => {
    removeDownloadItem(itemId)
  }

  const onDismiss = (itemId: string) => {
    removeDownloadItem(itemId)
  }

  const onFileNameChange = (itemId: string, fileName: string) => {
    updateDownloadItem(itemId, { fileName })
  }

  const onAutoDownloadToggle = (enabled: boolean) => {
    setAutoDownloadEnabled(enabled)
  }

  let appTitle: string
  let backgroundColor: string

  if (env.APP_ENV === ENVIRONMENTS.PRODUCTION) {
    appTitle = 'Axon Data Suite'
    backgroundColor = theme.palette.text.primary
  } else if (env.APP_ENV === ENVIRONMENTS.STAGING) {
    appTitle = 'Axon Data Suite - Staging'
    backgroundColor = theme.palette.secondary.main
  } else {
    let suffix = 'Local'
    if (env.APP_ENV) {
      suffix = `${env.APP_ENV}`
    }
    appTitle = `Axon Data Suite - Development - ${suffix}`
    backgroundColor = theme.palette.info.main
  }

  let mainText = 'Cell Visualization'
  if (isExternalDemo) {
    mainText = ''
  } else if (!sessionId && !demoEnabled) {
    mainText = 'Cell Visualization Saved Sessions'
  }

  const {
    cellVisualizations: {
      sessionData,
      max_cell_count: maxCellCount,
      leiden_resolution: leidenResolution,
      projection_params: projectionParams,
    },
  } = useCellVisualizationsSlice()
  const name = sessionData?.name

  const [open, setOpen] = React.useState(false)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)
  const isUMAP = projectionParams?.method !== 'tSNE'
  // Top-level projection_model_id is from classifier training; create_spec has it from session creation
  const projectionModelId =
    sessionData?.projection_model_id ?? sessionData?.create_spec?.projection_model_id

  const rowData: RowDataType[] = React.useMemo(() => {
    const data: RowDataType[] = []

    if (maxCellCount) {
      data.push({ label: 'Sample Size', values: maxCellCount })
    }
    if (leidenResolution) {
      data.push({ label: 'Leiden Resolution', values: leidenResolution })
    }

    const projectionType = isUMAP ? 'UMAP' : 'tSNE'
    data.push({ label: 'Projection Type', values: projectionType })

    if (projectionModelId) {
      data.push({ label: 'Projection Model', values: projectionModelId })
    }

    if (isUMAP && projectionParams) {
      if (projectionParams.num_neighbors) {
        data.push({ label: 'UMAP: # of Neighbors', values: projectionParams.num_neighbors })
      }
      if (projectionParams.min_dist) {
        data.push({ label: 'UMAP: Minimum Distance', values: projectionParams.min_dist })
      }
      if (projectionParams.spread) {
        data.push({ label: 'UMAP: Spread', values: projectionParams.spread })
      }
    } else if (projectionParams) {
      if (projectionParams.perplexity) {
        data.push({ label: 'tSNE: Perplexity', values: projectionParams.perplexity })
      }
      if (projectionParams.exaggeration) {
        data.push({ label: 'tSNE: Early Exaggeration', values: projectionParams.exaggeration })
      }
    }

    return data
  }, [maxCellCount, leidenResolution, isUMAP, projectionModelId, projectionParams])
  // Set page title for analytics tracking
  const pageTitle = isPreFilter
    ? 'Axon Data Suite - Cell Visualization: Prefilter'
    : 'Axon Data Suite - Cell Visualization'

  return (
    <TopBarBase
      backgroundColor={backgroundColor}
      toolbarProps={{ sx: { justifyContent: 'space-between' } }}
    >
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <ToolbarBox>
        {!isExternalDemo && <TopNavMenu />}
        <img
          alt=""
          src="/images/Deepcell-Icon-White.png"
          width="15px"
          style={{ marginRight: '15px' }}
        />
        <Typography variant="h6" noWrap width="100%">
          {appTitle} / {mainText}
        </Typography>
      </ToolbarBox>
      {sessionId && !demoEnabled ? (
        <Box display="flex" alignItems="center">
          <Typography variant="h6" noWrap>
            {name ?? 'Untitled Session'}
          </Typography>
          <IconButton
            size="small"
            onClick={handleOpen}
            sx={{
              ml: 1,
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: '1.2rem' }} />
          </IconButton>
          {open && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
              }}
              onClick={handleClose}
            >
              <Box sx={{ maxWidth: '500px', width: '100%' }} onClick={(e) => e.stopPropagation()}>
                <SectionCard title="Session Details" closeAction={handleClose} paddingCSS="14px">
                  <Stack spacing={1}>
                    {rowData.map((row, index) => (
                      <Box
                        key={row.label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          backgroundColor: index % 2 === 0 ? '#F6F6F6' : '#FFFFFF',
                          borderRadius: '4px',
                          fontSize: '12px',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 'normal', color: '#172623' }}>
                          {row.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#172623' }}>
                          {String(row.values)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </SectionCard>
              </Box>
            </Box>
          )}
        </Box>
      ) : null}
      {isPreFilter || errored ? null : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Download Indicator */}
          <DownloadIndicator
            downloads={downloads}
            onDownload={onDownload}
            onCancel={onCancel}
            onDismiss={onDismiss}
            onFileNameChange={onFileNameChange}
            autoDownload={autoDownload}
            onAutoDownloadToggle={onAutoDownloadToggle}
          />
          <NavTopBarRightSideButtons />
        </Box>
      )}
    </TopBarBase>
  )
}

export default CellVisualizationNavTopBar
